import { api } from "./client";

export type DemoKind = "video" | "audio" | "image" | "pdf" | "other";

export interface Demo {
  id: string;
  title: string;
  description: string | null;
  product: string | null;
  kind: DemoKind;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  accounts: { id: string; name: string }[];
}

export interface DemoUploadInput {
  file: File;
  title?: string;
  description?: string;
  product?: string;
  accountIds?: string[];
}

export const Demos = {
  list: (params?: { account_id?: string; q?: string }) =>
    api.get<Demo[]>("/demos", { params }).then((r) => r.data),
  get: (id: string) => api.get<Demo>(`/demos/${id}`).then((r) => r.data),
  upload: (input: DemoUploadInput, onProgress?: (fraction: number) => void) => {
    const form = new FormData();
    form.append("file", input.file);
    if (input.title) form.append("title", input.title);
    if (input.description) form.append("description", input.description);
    if (input.product) form.append("product", input.product);
    if (input.accountIds?.length) form.append("account_ids", input.accountIds.join(","));
    return api
      .post<Demo>("/demos", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && onProgress?.(e.loaded / e.total),
      })
      .then((r) => r.data);
  },
  update: (
    id: string,
    payload: { title?: string; description?: string | null; product?: string | null },
  ) => api.patch<Demo>(`/demos/${id}`, payload).then((r) => r.data),
  setAccounts: (id: string, accountIds: string[]) =>
    api.put<Demo>(`/demos/${id}/accounts`, { account_ids: accountIds }).then((r) => r.data),
  remove: (id: string) => api.delete(`/demos/${id}`),
  /** A short-lived URL that media players can load (they can't send headers). */
  link: (id: string) =>
    api.get<{ url: string; expires_in: number }>(`/demos/${id}/link`).then((r) => r.data),
};

/** 1536 → "1.5 KB". */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n < 10 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

export const MAX_DEMO_BYTES = 500 * 1024 * 1024;
